/////////////////////////////////////////////////////////////////////////////////////////////
// Copyright 2026 Garmin International, Inc.
// Licensed under the Flexible and Interoperable Data Transfer (FIT) Protocol License; you
// may not use this file except in compliance with the Flexible and Interoperable Data
// Transfer (FIT) Protocol License.
/////////////////////////////////////////////////////////////////////////////////////////////
// ****WARNING****  This file is auto-generated!  Do NOT edit this file.
// Profile Version = 21.218.0Release
// Tag = production/release/21.218.0-0-g96fee6d7
/////////////////////////////////////////////////////////////////////////////////////////////

package com.garmin.fit

/**
 * Reads a field's values as a little-endian bit stream, least significant bit
 * first.
 *
 * This is how FIT unpacks *components*: one stored field can pack several
 * logical fields at arbitrary bit widths (`compressed_speed_distance` carries a
 * 12-bit speed and a 12-bit distance in three bytes).
 */
public class BitStream(
    private val values: List<Any?>,
    private val baseType: BaseType,
) {
    public constructor(value: Any?, baseType: BaseType) : this(listOf(value), baseType)

    private val bitsPerValue: Int = baseType.size * 8

    private var bytes: ByteArray? = null
    private var position: Int = 0
    private var currentByte: Int = 0
    private var currentBit: Int = 8

    public var bitsAvailable: Int = bitsPerValue * values.size
        private set

    public val hasBitsAvailable: Boolean get() = bitsAvailable > 0

    private fun materialize(): ByteArray =
        bytes ?: ByteArray(values.size * baseType.size).also { array ->
            for ((i, value) in values.withIndex()) {
                val raw = FitValues.rawBits(value)
                for (b in 0 until baseType.size) {
                    array[i * baseType.size + b] = ((raw shr (8 * b)) and 0xFFuL).toByte()
                }
            }
            bytes = array
        }

    private fun readBit(): Int {
        if (!hasBitsAvailable) throw FitFormatException("bit stream exhausted")
        val array = materialize()
        if (currentBit >= 8) {
            if (position >= array.size) throw FitFormatException("bit stream exhausted")
            currentByte = array[position].toInt() and 0xFF
            position++
            currentBit = 0
        }
        val bit = currentByte and 0x01
        currentByte = currentByte shr 1
        currentBit++
        bitsAvailable--
        return bit
    }

    /**
     * Reads [count] bits into a [Long].
     *
     * When [signed] is set the result is sign-extended from bit `count - 1`,
     * which is what a component targeting a SINT field needs.
     */
    public fun readBits(count: Int, signed: Boolean = false): Long {
        require(count in 1..64) { "cannot read $count bits; the range is 1..64" }
        var value = 0L
        for (i in 0 until count) {
            value = value or (readBit().toLong() shl i)
        }
        return if (signed) signExtend(value, count) else value
    }

    public fun reset() {
        bytes = null
        position = 0
        currentByte = 0
        currentBit = 8
        bitsAvailable = bitsPerValue * values.size
    }

    private companion object {
        fun signExtend(value: Long, bits: Int): Long {
            if (bits <= 0 || bits >= 64) return value
            val shift = 64 - bits
            return (value shl shift) shr shift
        }
    }
}
